import { Select } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ListadoPlanes, CambiarEstadoPlan } from "../../../Service/Service.Plan";
import { ControllerListarSegurosClinica } from "../../../Service/Services.seguros";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import usePermissions from "../../../hook/usePermissions";
import { calcularMontosCobertura } from "../../../utils/copagoCalculo";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda } from "../../UI/DataTable/piezas";
import { ChipEstado } from "../../UI/Chip";
import { CampoTexto } from "../../UI/Campo";
import MenuAcciones from "../../UI/MenuAcciones";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { IconoEditar, IconoBuscar, IconoCheck, IconoProhibido } from "../../UI/Iconos";

// CONFIG-PLANES-SEGURO-31: administración de planes con selector de seguro +
// búsqueda + filtro de estado. "Eliminar" se retira: los planes se inactivan
// (plan.estado) sin borrar la fila, para preservar facturas / prestaciones /
// resultados / reportes / pacientes históricos.
const ESTADO_OPCIONES = [
    { value: "Activo", label: "Activos" },
    { value: "Inactivo", label: "Inactivos" },
    { value: "Todos", label: "Todos" },
];

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function PlanesListado({ data }) {
    // RBAC-CUTOVER-07: Editar/Inactivar dependen de planes.edit (backend ya lo
    // exige en planes.routes.ts, incl. PATCH /planes/:id/estado).
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [listarPlanes, setListarPlanes] = useState([]);
    const [error, setError] = useState(null);

    const [seguros, setSeguros] = useState([]);
    const [seguroId, setSeguroId] = useState(null);       // null = Todos
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");             // valor con debounce
    const [estado, setEstado] = useState("Activo");       // default: Activos
    // Qué cambio de estado se está confirmando: { plan, nuevoEstado }
    const [porCambiar, setPorCambiar] = useState(null);
    const [cambiando, setCambiando] = useState(false);
    const { mostrar } = useToast();

    const { reloadPromociones, onEditarPlan } = data;

    const handleEdit = (record) => {
        if (onEditarPlan) onEditarPlan(record);
    };

    const handleCambiarEstado = async (record, nuevoEstado) => {
        setCambiando(true);
        try {
            const response = await CambiarEstadoPlan(record.idplan, nuevoEstado);
            if (response.success) {
                mostrar({ tipo: "exito", texto: response.message || "Estado actualizado" });
                fetchInitialData();
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            mostrar({ tipo: "error", texto: error.message || "No se pudo cambiar el estado del plan" });
        } finally {
            setCambiando(false);
            setPorCambiar(null);
        }
    };

    const columnas = [
        { key: "nombre", titulo: "Plan", render: (p) => <Celda valor={p.nombre} /> },
        {
            key: "seguro",
            titulo: "Seguro/Entidad",
            ancho: 220,
            render: (p) => <Celda valor={p.Seguros?.nombre} />,
        },
        {
            key: "estado",
            titulo: "Estado",
            ancho: 120,
            render: (p) => (
                <ChipEstado tono={p.estado === "Inactivo" ? "anulado" : "hecho"}>
                    {p.estado === "Inactivo" ? "Inactivo" : "Activo"}
                </ChipEstado>
            ),
        },
        {
            key: "acciones",
            titulo: "",
            ancho: 60,
            alinear: "centro",
            render: (p) => {
                if (!hasPermission("planes.edit")) return null;
                const inactivo = p.estado === "Inactivo";
                return (
                    <MenuAcciones
                        titulo={`Acciones de ${p.nombre}`}
                        acciones={[
                            { label: "Editar", icono: IconoEditar, onClick: () => handleEdit(p) },
                            inactivo
                                ? {
                                      label: "Activar",
                                      icono: IconoCheck,
                                      onClick: () => setPorCambiar({ plan: p, nuevoEstado: "Activo" }),
                                  }
                                : {
                                      label: "Inactivar",
                                      icono: IconoProhibido,
                                      peligro: true,
                                      onClick: () => setPorCambiar({ plan: p, nuevoEstado: "Inactivo" }),
                                  },
                        ]}
                    />
                );
            },
        },
    ];

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        const params = { estado };
        if (seguroId) params.seguro_id = seguroId;
        if (search) params.search = search;

        const planes = await ListadoPlanes(params);

        if (planes?.data) {
            const listado = planes.data.map((item) => ({
                ...item,
                idplan: item.id_plan,
                key: item.id_plan,
            }));
            setListarPlanes(listado);
            setError(null);
        } else {
            // Antes un fallo dejaba la tabla vacía, igual que un filtro sin
            // resultados: dos cosas distintas con la misma pinta.
            setListarPlanes([]);
            setError("No se pudo obtener el listado de planes.");
        }
        setLoading(false);
    }, [estado, seguroId, search]);

    // Carga del selector de seguros (solo seguros activos de la clínica).
    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const segs = await ControllerListarSegurosClinica(idclinica);
            setSeguros((segs || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
        })();
    }, []);

    // Debounce de la búsqueda por nombre.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData, reloadPromociones]);

    const seguroOptions = useMemo(
        () => [{ value: null, label: "Todos los seguros" }, ...seguros],
        [seguros]
    );

    const hayFiltros = Boolean(seguroId) || Boolean(search) || estado !== "Activo";

    return (
        <>
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                <div style={{ minWidth: 220 }}>
                    <label className="kc__etiqueta" htmlFor="plan-filtro-seguro">Seguro</label>
                    <Select
                        id="plan-filtro-seguro"
                        className="cfg-plan-filtro-seguro w-100"
                        value={seguroId}
                        onChange={(v) => setSeguroId(v ?? null)}
                        options={seguroOptions}
                        showSearch
                        optionFilterProp="label"
                        placeholder="Seguro"
                        aria-label="Filtrar por seguro"
                    />
                </div>
                {/* PLANES-SEARCH-UI-FIX-32: el buscador es UNA sola caja, con el
                    icono dentro. Sin cambios de comportamiento: mismo onChange
                    -> mismo debounce de 300ms -> misma búsqueda server-side. */}
                <div style={{ width: 260, maxWidth: "100%" }}>
                    <CampoTexto
                        etiqueta="Buscar plan"
                        icono={IconoBuscar}
                        placeholder="Buscar plan..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        aria-label="Buscar plan"
                        data-testid="plan-buscar"
                    />
                </div>
                <div style={{ minWidth: 140 }}>
                    <label className="kc__etiqueta" htmlFor="plan-filtro-estado">Estado</label>
                    <Select
                        id="plan-filtro-estado"
                        className="w-100"
                        value={estado}
                        onChange={setEstado}
                        options={ESTADO_OPCIONES}
                        aria-label="Filtrar por estado"
                    />
                </div>
            </div>

            <DataTable
                columnas={columnas}
                filas={listarPlanes}
                filaKey="id_plan"
                cargando={loading}
                error={error}
                onReintentar={fetchInitialData}
                etiquetaTabla="Planes"
                hayFiltrosAplicados={hayFiltros}
                onLimpiarFiltros={() => {
                    setSeguroId(null);
                    setSearchInput("");
                    setEstado("Activo");
                }}
                textoVacioSinFiltros="Todavía no hay planes."
                expansion={{
                    expandible: (p) => !!p.detalles,
                    render: (p) =>
                        p.detalles.length > 0 ? (
                            <ol className="cfg-detalle">
                                {p.detalles.map((detalle, index) => {
                                    // COBERTURAS-COPAGO-CALCULO-AUDIT-FIX-01: reparto DERIVADO de (copago, tarifa)
                                    const montos = calcularMontosCobertura(detalle.tarifario.precio, detalle.copago);
                                    return (
                                        <li className="cfg-detalle__fila" key={index}>
                                            <div>
                                                <div className="cfg-detalle__titulo">
                                                    {detalle.ExamenTipo.codigo} : {detalle.ExamenTipo.descripcion}
                                                </div>
                                                <span className="cfg-detalle__dato">
                                                    P. Normal: {money(detalle.tarifario.precio)} · Copago: {detalle.copago}%
                                                    <br />
                                                    Paga Paciente: {money(montos.precioPaciente)} · Paga Empresa:{" "}
                                                    {money(montos.precioEmpresa)}
                                                </span>
                                            </div>
                                            <span className="cfg-detalle__destacado">
                                                {detalle.ExamenTipo.categoria.Descripcion}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        ) : (
                            <p className="mb-0">No hay detalles que mostrar...</p>
                        ),
                }}
            />

            {/* El texto de las dos confirmaciones es EL MISMO que tenían los
                `Popconfirm`: dice qué deja de estar disponible y qué se
                conserva. Eso no se toca, se conserva entero. */}
            <Modal
                abierto={Boolean(porCambiar)}
                titulo={porCambiar?.nuevoEstado === "Activo" ? "¿Activar este plan?" : "¿Inactivar este plan?"}
                destructivo={porCambiar?.nuevoEstado === "Inactivo"}
                onCerrar={() => setPorCambiar(null)}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: porCambiar?.nuevoEstado === "Activo" ? "Activar" : "Inactivar",
                    cargando: cambiando,
                    onClick: () => handleCambiarEstado(porCambiar.plan, porCambiar.nuevoEstado),
                }}
            >
                <p className="mb-0">
                    <b>{porCambiar?.plan?.nombre}</b>.{" "}
                    {porCambiar?.nuevoEstado === "Activo"
                        ? "El plan volverá a estar disponible para nuevas operaciones."
                        : "El plan dejará de estar disponible para nuevas operaciones. Las facturas, prestaciones, resultados y demás registros históricos que ya lo utilizan conservarán su información."}
                </p>
            </Modal>
        </>
    );
}

export default PlanesListado;
